/**
 * NUTRIOP LONGEVITY - ENHANCED CART INTERACTIONS
 * Version: 3.0 - Premium UX with Dynamic Free Shipping Progress Bar
 * All original functionality preserved with enhanced UI/UX
 */

// Enhanced Cart Remove Button with animation
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      
      // Add removal animation
      const cartItem = this.closest('.cart-item');
      if (cartItem) {
        cartItem.style.animation = 'fadeOutScale 0.4s ease-in-out';
        cartItem.style.opacity = '0.5';
      }

      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");

      const observer = new MutationObserver(() => {
        if (!cartItems.querySelector("[data-index]")) {
          updateCartLayout();
          // Trigger success animation on empty cart
          const emptyText = document.querySelector('.cart-empty-text');
          if (emptyText) {
            emptyText.style.animation = 'bounceIn 0.6s ease';
          }
          observer.disconnect();
        }
      });

      observer.observe(cartItems, { childList: true, subtree: true });
      cartItems.updateQuantity(this.dataset.index, 0);
      
      // Haptic feedback for mobile
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

// Enhanced Cart Items with improved loading states
class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
    
    // Add animation on load
    this.style.opacity = '0';
    requestAnimationFrame(() => {
      this.style.transition = 'opacity 0.5s ease';
      this.style.opacity = '1';
    });
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );
    
    // Initialize quantity input enhancements
    this.enhanceQuantityInputs();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  // New method: Enhance quantity inputs with +/- buttons
  enhanceQuantityInputs() {
    const quantityInputs = this.querySelectorAll('input[type="number"]');
    quantityInputs.forEach(input => {
      if (input.dataset.enhanced) return;
      input.dataset.enhanced = 'true';
      
      // Add increment/decrement on scroll
      input.addEventListener('wheel', (e) => {
        e.preventDefault();
        const delta = e.deltaY < 0 ? 1 : -1;
        const newValue = parseInt(input.value) + delta;
        if (newValue >= 0) {
          input.value = newValue;
          input.dispatchEvent(new Event('change'));
        }
      });
    });
  }

  onChange(event) {
    // Add visual feedback
    const input = event.target;
    input.style.transform = 'scale(1.05)';
    setTimeout(() => {
      input.style.transform = 'scale(1)';
    }, 200);

    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute("name")
    );
  }

  onCartUpdate() {
    // Check if the cart-items custom element exists
    const cartItems = document.querySelector('cart-items');

    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          if (typeof AOS !== "undefined") {
            AOS.init();
          }
        })  
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          if (sourceQty) {
            this.innerHTML = sourceQty.innerHTML;
            // Re-enhance after update
            this.enhanceQuantityInputs();
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }

    // Reload the page if the cart-items element is present
    if (cartItems) {
      window.location.reload();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-contents",
      },
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          
          // Shake animation for error
          quantityElement.style.animation = 'shake 0.5s ease';
          setTimeout(() => {
            quantityElement.style.animation = '';
          }, 500);
          
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        
        // Success animation
        const cartFooterElement = document.querySelector('.cart__footer');
        if (cartFooterElement) {
          cartFooterElement.style.animation = 'pulseSuccess 0.6s ease';
          setTimeout(() => {
            cartFooterElement.style.animation = '';
          }, 600);
        }
        
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".drawer-inner-empty"),
            cartDrawerWrapper.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item-name")
          );
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });
      })
      .catch(() => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item-error-text").innerHTML = message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => {
      overlay.classList.remove("hidden");
      // Enhanced loading animation
      overlay.style.animation = 'fadeIn 0.3s ease';
    });

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    cartItemElements.forEach((overlay) => {
      overlay.style.animation = 'fadeOut 0.3s ease';
      setTimeout(() => overlay.classList.add("hidden"), 300);
    });
    cartDrawerItemElements.forEach((overlay) => {
      overlay.style.animation = 'fadeOut 0.3s ease';
      setTimeout(() => overlay.classList.add("hidden"), 300);
    });
  }
}

customElements.define("cart-items", CartItems);

// Enhanced Cart Note with character counter
if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();
        
        const noteInput = this.querySelector('textarea, input');
        
        // Add character counter
        if (noteInput) {
          const counter = document.createElement('div');
          counter.className = 'cart-note-counter';
          counter.style.cssText = 'text-align: right; font-size: 0.85em; color: rgba(0,0,0,0.5); margin-top: 5px;';
          this.appendChild(counter);
          
          noteInput.addEventListener('input', (event) => {
            const length = event.target.value.length;
            const maxLength = noteInput.maxLength || 500;
            counter.textContent = `${length} / ${maxLength}`;
            
            // Color code based on length
            if (length > maxLength * 0.9) {
              counter.style.color = '#e74c3c';
            } else if (length > maxLength * 0.7) {
              counter.style.color = '#f39c12';
            } else {
              counter.style.color = 'rgba(0,0,0,0.5)';
            }
          });
        }

        this.addEventListener(
          "change",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            }).then(() => {
              // Success feedback
              event.target.style.borderColor = '#56ab2f';
              setTimeout(() => {
                event.target.style.borderColor = '';
              }, 1000);
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// Enhanced Terms Checkbox with visual feedback
class TermsCheckbox extends HTMLElement {
  constructor() {
    super();
    this.checkbox = null;
    this.checkoutButtons = null;
  }

  connectedCallback() {
    this.checkbox = this.querySelector("#termsCheckbox");
    this.checkoutButtons = document.querySelectorAll(".cart-checkout-button");

    this.restoreCheckboxState();
    this.checkbox.addEventListener("change", this.updateState.bind(this));

    // Enhanced checkbox styling
    if (this.checkbox) {
      const label = this.querySelector('label');
      if (label) {
        label.style.cursor = 'pointer';
        label.style.transition = 'color 0.3s ease';
        
        this.checkbox.addEventListener('change', () => {
          if (this.checkbox.checked) {
            label.style.color = '#56ab2f';
            // Celebrate checking
            this.checkoutButtons.forEach(button => {
              button.style.animation = 'bounceIn 0.6s ease';
            });
          } else {
            label.style.color = '';
          }
        });
      }
    }

    // Subscribe to cart updates
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      this.reinitializeCheckbox.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  updateState() {
    if (this.checkoutButtons.length) {
      this.checkoutButtons.forEach((button) => {
        if (this.checkbox.checked) {
          button.removeAttribute("disabled");
          button.style.animation = 'pulseSuccess 0.6s ease';
        } else {
          button.setAttribute("disabled", "disabled");
          button.style.animation = 'shake 0.5s ease';
        }
      });
      if (this.checkbox.checked) {
        this.checkoutButtons[0].focus();
      }
    }
    this.saveCheckboxState();
  }

  saveCheckboxState() {
    localStorage.setItem(
      "termsCheckboxState",
      this.checkbox.checked ? "checked" : "unchecked"
    );
  }

  restoreCheckboxState() {
    const savedState = localStorage.getItem("termsCheckboxState");
    this.checkbox.checked = savedState === "checked";
    this.updateState();
  }

  reinitializeCheckbox() {
    this.checkbox = this.querySelector("#termsCheckbox");
    this.restoreCheckboxState();

    if (typeof AOS !== "undefined") {
      AOS.init();
    }
  }
}

customElements.define("terms-checkbox", TermsCheckbox);

// Enhanced Custom Dropdown with smooth animations
class CustomDropdown extends HTMLElement {
  constructor() {
    super();
    this.dropdownHeader = this.querySelector(".dropdown-header");
    this.dropdownContent = this.querySelector(".dropdown-content");

    if (this.dropdownHeader && this.dropdownContent) {
      this.dropdownHeader.addEventListener("click", () =>
        this.toggleDropdown()
      );
      
      // Add hover effect
      this.dropdownHeader.addEventListener('mouseenter', () => {
        this.dropdownHeader.style.transform = 'translateX(5px)';
      });
      
      this.dropdownHeader.addEventListener('mouseleave', () => {
        this.dropdownHeader.style.transform = '';
      });
    }
  }
  
  toggleDropdown() {
    const isOpen = this.dropdownContent.classList.contains("open");

    // Close all dropdowns with animation
    document
      .querySelectorAll("custom-dropdown .dropdown-content")
      .forEach((content) => {
        content.style.animation = 'collapseUp 0.3s ease';
        setTimeout(() => {
          content.classList.remove("open");
        }, 300);
      });
    document
      .querySelectorAll("custom-dropdown .dropdown-header")
      .forEach((header) => {
        header.classList.remove("open");
      });

    // Open the current one with animation
    if (!isOpen) {
      this.dropdownContent.style.animation = 'expandDown 0.4s ease';
      this.dropdownContent.classList.add("open");
      this.dropdownHeader.classList.add("open");
    }
    
    if (typeof AOS !== "undefined") {
      AOS.init();
    }
  }
  
  connectedCallback() {}
  
  disconnectedCallback() {
    if (this.dropdownHeader) {
      this.dropdownHeader.removeEventListener("click", this.toggleDropdown);
    }
  }
}
customElements.define("custom-dropdown", CustomDropdown);

// ENHANCED Shipping Message - Geographic Free Shipping with Variable Thresholds
class ShippingMessage extends HTMLElement {
  constructor() {
    super();
    this.isAnimating = false;
    this.celebrationTriggered = false;
    
    // TIER 1: Countries with FREE SHIPPING (no threshold required)
    // NOW EMPTY - All countries moved to custom thresholds
    this.freeShippingCountries = [];
    
    // TIER 2: Countries with CUSTOM FREE SHIPPING THRESHOLDS
    // Based on actual Shopify shipping rates
    this.customThresholds = {
      // €50 threshold - Featured Premium Tier (UK, EU, US, Switzerland)
      'GB': 50,  // United Kingdom
      'CH': 50,  // Switzerland
      'US': 50,  // United States
      // EU Countries - All now at €50
      'AT': 50,  // Austria
      'BE': 50,  // Belgium
      'BG': 50,  // Bulgaria
      'HR': 50,  // Croatia
      'CY': 50,  // Cyprus
      'CZ': 50,  // Czech Republic
      'DK': 50,  // Denmark
      'EE': 50,  // Estonia
      'FI': 50,  // Finland
      'FR': 50,  // France
      'DE': 50,  // Germany
      'GR': 50,  // Greece
      'HU': 50,  // Hungary
      'IE': 50,  // Ireland
      'IT': 50,  // Italy
      'LV': 50,  // Latvia
      'LT': 50,  // Lithuania
      'LU': 50,  // Luxembourg
      'MT': 50,  // Malta
      'NL': 50,  // Netherlands
      'PL': 50,  // Poland
      'PT': 50,  // Portugal
      'RO': 50,  // Romania
      'SK': 50,  // Slovakia
      'SI': 50,  // Slovenia
      'ES': 50,  // Spain
      'SE': 50,  // Sweden
      
      // €50 threshold - Channel Islands
      'GG': 50,  // Guernsey
      'JE': 50,  // Jersey
      'GI': 50,  // Gibraltar
      
      // €100 threshold - Greater Europe
      'AD': 100, // Andorra
      'BA': 100, // Bosnia & Herzegovina
      'MD': 100, // Moldova
      'SM': 100, // San Marino
      'RS': 100, // Serbia
      
      // €150 threshold - Worldwide
      'NZ': 150, // New Zealand
      'IS': 150, // Iceland
      'IL': 150, // Israel
      
      // €300 threshold - Premium Routes
      'CA': 300, // Canada
      'HK': 300, // Hong Kong
      'SG': 300, // Singapore
      'TW': 300, // Taiwan
      'AE': 300, // United Arab Emirates
      'NO': 300, // Norway
      
      // €500 threshold - Down Under
      'AU': 500, // Australia
      
      // Add more countries here as needed
      // 'XX': amount,
    };
    
    // TIER 3: Countries with NO FREE SHIPPING (show cost savings instead)
    this.noFreeShippingCountries = [
      'GL', // Greenland - no free shipping option
      // Add other countries here that don't offer free shipping
      // 'XX',
    ];
    
    // TIER 4: Default threshold for all other countries
    this.defaultThreshold = 150;
  }

  connectedCallback() {
    this.render();
    // Re-render on cart updates
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      () => {
        this.render();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  /**
   * Detect customer's country from multiple sources
   */
  detectCustomerCountry() {
    // Method 1: Check Shopify's geolocation (most reliable)
    if (typeof Shopify !== 'undefined' && Shopify.country) {
      return Shopify.country.toUpperCase();
    }
    
    // Method 2: Check if customer is logged in with a default address
    if (typeof Shopify !== 'undefined' && Shopify.customer && Shopify.customer.default_address) {
      return Shopify.customer.default_address.country_code.toUpperCase();
    }
    
    // Method 3: Try to get from cart attributes (if set by your theme)
    const cartCountry = this.getAttribute('data-customer-country');
    if (cartCountry) {
      return cartCountry.toUpperCase();
    }
    
    // Method 4: Fallback - try to detect from browser locale (least reliable)
    try {
      const locale = navigator.language || navigator.userLanguage;
      if (locale && locale.includes('-')) {
        return locale.split('-')[1].toUpperCase();
      }
    } catch (e) {
      console.log('Could not detect country from browser locale');
    }
    
    // Default: return null if unable to detect
    return null;
  }

  /**
   * Check if country has free shipping option at all
   */
  hasNoFreeShipping() {
    const customerCountry = this.detectCustomerCountry();
    if (!customerCountry) return false;
    
    return this.noFreeShippingCountries.includes(customerCountry);
  }

  /**
   * Get the free shipping threshold for customer's country
   * Returns 0 for countries with automatic free shipping
   * Returns custom threshold for countries with variable thresholds
   * Returns default threshold (150) for all other countries
   * Returns -1 for countries with no free shipping
   */
  getFreeShippingThreshold() {
    const customerCountry = this.detectCustomerCountry();
    
    // If we can't detect country, use default threshold
    if (!customerCountry) {
      return this.defaultThreshold;
    }
    
    // Check if country has NO free shipping option
    if (this.noFreeShippingCountries.includes(customerCountry)) {
      return -1; // No free shipping available
    }
    
    // Check if country has automatic free shipping (Tier 1)
    if (this.freeShippingCountries.includes(customerCountry)) {
      return 0; // No threshold needed
    }
    
    // Check if country has custom threshold (Tier 2)
    if (this.customThresholds.hasOwnProperty(customerCountry)) {
      return this.customThresholds[customerCountry];
    }
    
    // Otherwise use default threshold (Tier 3)
    return this.defaultThreshold;
  }

  /**
   * Format currency using Shopify's currency system
   */
  formatCurrency(amount, currency) {
    const options = { style: 'currency', currency: currency };
    return new Intl.NumberFormat('en-US', options).format(amount);
  }

  render() {
    this.innerHTML = '';
    
    const customerCountry = this.detectCustomerCountry();
    const thresholdAmount = this.getFreeShippingThreshold();
    
    // SPECIAL CASE: Countries with NO free shipping option
    if (thresholdAmount === -1) {
      this.renderNoFreeShippingMessage(customerCountry);
      return;
    }
    
    // TIER 1: Show "Free Shipping Unlocked" for automatic free shipping countries
    if (thresholdAmount === 0) {
      const freeShippingMessage = document.createElement('div');
      freeShippingMessage.className = 'shipping-message-free';
      freeShippingMessage.style.cssText = `
        position: relative;
        padding: 15px;
        background: linear-gradient(135deg, rgba(86, 171, 47, 0.1) 0%, rgba(168, 224, 99, 0.1) 100%);
        border-radius: 12px;
        box-shadow: 0 4px 15px rgba(0,0,0,0.08);
        border-left: 4px solid #56ab2f;
      `;
      
      freeShippingMessage.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; gap: 10px; flex-wrap: wrap;">
          <div style="font-size: 1.5em; animation: celebrate 0.6s ease;">🎉</div>
          <span style="
            color: #56ab2f; 
            font-weight: 700;
            font-size: 1.1em;
            text-align: center;
          ">
            🎁 Nice! You're getting FREE delivery
          </span>
          <div style="font-size: 1.5em; animation: celebrate 0.6s ease 0.2s;">🚚</div>
        </div>
        <div style="
          text-align: center; 
          font-size: 0.9em; 
          color: #666; 
          margin-top: 8px;
          font-weight: 500;
        ">
          ✓  All your money goes to your wellness, not shipping • ${this.getCountryName(customerCountry)} exclusive
        </div>
      `;
      
      this.appendChild(freeShippingMessage);
      this.injectStyles();
      return; // Exit early
    }
    
    // TIER 2 & 3: Show progress bar with appropriate threshold
    const cartTotal = parseFloat(this.getAttribute('data-cart-total'));
    const messageBefore = this.getAttribute('data-message-before') || 'Spend ';
    const messageAfter = this.getAttribute('data-message-after') || ' more for FREE SHIPPING';
    const messageSuccess = this.getAttribute('data-message-success') || 'You\'ve unlocked FREE shipping!';
    const currencyRate = Shopify.currency.rate || 1;
    const activeCurrency = Shopify.currency.active || 'EUR';

    // Convert threshold to customer's currency
    const convertedThreshold = thresholdAmount * currencyRate;
    let remainingAmount = Math.max(0, convertedThreshold - cartTotal);
    const progressPercentage = Math.min((cartTotal / convertedThreshold) * 100, 100);
    const isQualified = progressPercentage >= 100;

    remainingAmount = remainingAmount.toFixed(2);

    const container = document.createElement('div');
    container.className = 'shipping-message-container';
    container.style.cssText = 'position: relative; padding: 15px; background: linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,249,250,0.95) 100%); border-radius: 12px; box-shadow: 0 4px 15px rgba(0,0,0,0.08);';

    // Progress bar container
    const progressBarContainer = document.createElement('div');
    progressBarContainer.className = 'shipping-progress-wrapper';
    progressBarContainer.style.cssText = 'position: relative; margin-bottom: 15px;';

    // Progress bar background
    const progressBar = document.createElement('div');
    progressBar.className = 'shipping-progress-bar';
    progressBar.style.cssText = `
      width: 100%;
      height: 20px;
      background: linear-gradient(135deg, rgba(86, 171, 47, 0.1) 0%, rgba(168, 224, 99, 0.1) 100%);
      border-radius: 10px;
      overflow: hidden;
      position: relative;
      box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
    `;

    // Progress fill
    const progressFill = document.createElement('div');
    progressFill.className = 'shipping-progress-fill';
    progressFill.style.cssText = `
      height: 100%;
      background: ${isQualified 
        ? 'linear-gradient(90deg, #56ab2f, #a8e063)' 
        : 'linear-gradient(90deg, #FF6B6B, #E30613)'};
      width: 0%;
      transition: width 1s cubic-bezier(0.4, 0, 0.2, 1), background 0.6s ease;
      border-radius: 10px;
      position: relative;
      box-shadow: ${isQualified 
        ? '0 2px 8px rgba(86, 171, 47, 0.4)' 
        : '0 2px 8px rgba(102, 126, 234, 0.3)'};
    `;

    // Animated stripes for progress fill
    const progressStripes = document.createElement('div');
    progressStripes.className = 'progress-stripes';
    progressStripes.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: linear-gradient(
        45deg,
        rgba(255,255,255,0.2) 25%,
        transparent 25%,
        transparent 50%,
        rgba(255,255,255,0.2) 50%,
        rgba(255,255,255,0.2) 75%,
        transparent 75%,
        transparent
      );
      background-size: 30px 30px;
      animation: ${isQualified ? 'moveStripes' : 'moveStripesSlower'} 1s linear infinite;
      border-radius: 10px;
    `;

    progressFill.appendChild(progressStripes);
    progressBar.appendChild(progressFill);

    // Progress percentage label
    const progressLabel = document.createElement('div');
    progressLabel.className = 'progress-label';
    progressLabel.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-weight: 700;
      font-size: 0.85em;
      color: ${progressPercentage > 50 ? '#fff' : '#333'};
      text-shadow: ${progressPercentage > 50 ? '0 1px 2px rgba(0,0,0,0.2)' : 'none'};
      z-index: 2;
      transition: color 0.3s ease;
    `;
    progressLabel.textContent = `${Math.floor(progressPercentage)}%`;

    progressBar.appendChild(progressLabel);

    // Truck icon (positioned on progress bar)
    const truckIcon = document.createElement('div');
    truckIcon.className = 'truck-icon';
    truckIcon.style.cssText = `
      position: absolute;
      top: 50%;
      left: ${Math.min(progressPercentage, 95)}%;
      transform: translate(-50%, -50%);
      font-size: 1.5em;
      z-index: 3;
      transition: left 1s cubic-bezier(0.4, 0, 0.2, 1);
      animation: ${isQualified ? 'truckBounce' : 'truckMove'} 2s ease infinite;
    `;
    truckIcon.textContent = '🚚';

    progressBarContainer.appendChild(progressBar);
    progressBarContainer.appendChild(truckIcon);
    container.appendChild(progressBarContainer);

    // Message element
    const messageElement = document.createElement('div');
    messageElement.className = 'shipping-message-text';
    messageElement.style.cssText = 'text-align: center; font-size: 1.05em; font-weight: 500;';
    
    if (!isQualified) {
      // Show different threshold message based on country with CONVERTED currency
      let thresholdNote = '';
      if (thresholdAmount !== this.defaultThreshold) {
        // Convert and format the threshold amount in customer's currency
        const formattedThreshold = this.formatCurrency(convertedThreshold, activeCurrency);
        thresholdNote = `<div style="font-size: 0.85em; color: #666; margin-top: 4px;">
          🌍 Free shipping for ${this.getCountryName(customerCountry)} at ${formattedThreshold}
        </div>`;
      }
      
      messageElement.innerHTML = `
        <span style="color: #333;">
          ${messageBefore}
          <span class="amount-bold" style="
            font-weight: 700;
            color: #FF6B6B;
            font-size: 1.2em;
            background: linear-gradient(135deg, #FF6B6B, #E30613);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          ">${this.formatCurrency(remainingAmount, activeCurrency)}</span>
          ${messageAfter}
        </span>
        ${thresholdNote}
      `;
    } else {
      // Celebration state
      messageElement.innerHTML = `
        <span style="
          color: #56ab2f; 
          font-weight: 700;
          font-size: 1.1em;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        ">
          <span style="animation: celebrate 0.6s ease;">🎁</span>
          ${messageSuccess}
          <span style="animation: celebrate 0.6s ease 0.2s;">🎁</span>
        </span>
      `;
      
      // Trigger confetti effect if just qualified
      if (!this.celebrationTriggered && progressPercentage >= 100) {
        this.celebrationTriggered = true;
        this.triggerCelebration(container);
      }
    }

    container.appendChild(messageElement);
    this.appendChild(container);

    // Add milestone indicators
    if (!isQualified) {
      this.addMilestoneIndicators(progressBar, progressPercentage);
    }

    // Animate progress bar fill
    requestAnimationFrame(() => {
      progressFill.style.width = `${progressPercentage}%`;
      
      // Add pulse effect when close to free shipping
      if (progressPercentage >= 75 && progressPercentage < 100) {
        progressFill.style.animation = 'almostThere 1.5s ease infinite';
      }
    });

    // Add styles if not already present
    this.injectStyles();
  }

  /**
   * Render message for countries with no free shipping option
   * Shows shipping cost information instead
   */
  renderNoFreeShippingMessage(customerCountry) {
    const messageContainer = document.createElement('div');
    messageContainer.className = 'shipping-message-no-free';
    messageContainer.style.cssText = `
      position: relative;
      padding: 15px;
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
      border-radius: 12px;
      box-shadow: 0 4px 15px rgba(0,0,0,0.08);
      border-left: 4px solid #667eea;
    `;
    
    messageContainer.innerHTML = `
      <div style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap;">
        <div style="font-size: 1.3em;">📦</div>
        <span style="
          color: #667eea; 
          font-weight: 600;
          font-size: 1em;
          flex: 1;
          min-width: 200px;
        ">
          Shipping costs for ${this.getCountryName(customerCountry)}
        </span>
      </div>
      <div style="
        text-align: center; 
        font-size: 0.9em; 
        color: #666; 
        margin-top: 10px;
        line-height: 1.5;
      ">
        Shipping rates apply based on order value and delivery speed.<br>
        <span style="font-weight: 600; color: #667eea;">Lower rates available for larger orders</span>
      </div>
    `;
    
    this.appendChild(messageContainer);
    this.injectStyles();
  }

  /**
   * Get friendly country name for display
   */
  getCountryName(countryCode) {
    const countryNames = {
      'GB': 'UK',
      'CH': 'Switzerland',
      'US': 'United States',
      'AU': 'Australia',
      'CA': 'Canada',
      'NZ': 'New Zealand',
      'SG': 'Singapore',
      'HK': 'Hong Kong',
      'TW': 'Taiwan',
      'AE': 'United Arab Emirates',
      'IL': 'Israel',
      'NO': 'Norway',
      'IS': 'Iceland',
      'AD': 'Andorra',
      'GG': 'Guernsey',
      'JE': 'Jersey',
      'GI': 'Gibraltar',
      'GL': 'Greenland',
      'BA': 'Bosnia & Herzegovina',
      'MD': 'Moldova',
      'SM': 'San Marino',
      'RS': 'Serbia',
      'DE': 'Germany',
      'FR': 'France',
      'IT': 'Italy',
      'ES': 'Spain',
      'NL': 'Netherlands',
      'BE': 'Belgium',
      'AT': 'Austria',
      'SE': 'Sweden',
      'DK': 'Denmark',
      'FI': 'Finland',
      'IE': 'Ireland',
      'PL': 'Poland',
      'PT': 'Portugal',
      'CZ': 'Czech Republic',
      'GR': 'Greece',
      'HU': 'Hungary',
      'RO': 'Romania',
      'BG': 'Bulgaria',
      'HR': 'Croatia',
      'SK': 'Slovakia',
      'SI': 'Slovenia',
      'LT': 'Lithuania',
      'LV': 'Latvia',
      'EE': 'Estonia',
      'CY': 'Cyprus',
      'LU': 'Luxembourg',
      'MT': 'Malta'
    };
    return countryNames[countryCode] || `${countryCode} customers`;
  }

  addMilestoneIndicators(progressBar, currentProgress) {
    const milestones = [25, 50, 75];
    milestones.forEach(milestone => {
      const indicator = document.createElement('div');
      indicator.className = 'milestone-indicator';
      indicator.style.cssText = `
        position: absolute;
        left: ${milestone}%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 4px;
        height: 12px;
        background: ${currentProgress >= milestone ? '#56ab2f' : 'rgba(0,0,0,0.2)'};
        border-radius: 2px;
        z-index: 1;
        transition: background 0.3s ease;
      `;
      progressBar.appendChild(indicator);
    });
  }

  triggerCelebration(container) {
    // Create confetti particles
    const colors = ['#56ab2f', '#a8e063', '#667eea', '#764ba2', '#ffd700'];
    for (let i = 0; i < 20; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti';
      confetti.style.cssText = `
        position: absolute;
        width: 10px;
        height: 10px;
        background: ${colors[Math.floor(Math.random() * colors.length)]};
        top: 50%;
        left: 50%;
        border-radius: ${Math.random() > 0.5 ? '50%' : '0'};
        animation: confettiFall 1s ease-out forwards;
        animation-delay: ${Math.random() * 0.3}s;
        z-index: 10;
      `;
      container.appendChild(confetti);
      
      // Remove confetti after animation
      setTimeout(() => confetti.remove(), 1300);
    }
  }

  injectStyles() {
    if (document.querySelector('#shipping-progress-animations')) return;
    
    const style = document.createElement('style');
    style.id = 'shipping-progress-animations';
    style.textContent = `
      @keyframes moveStripes {
        0% { background-position: 0 0; }
        100% { background-position: 30px 0; }
      }
      
      @keyframes moveStripesSlower {
        0% { background-position: 0 0; }
        100% { background-position: 30px 0; }
      }
      
      @keyframes truckMove {
        0%, 100% { transform: translate(-50%, -50%) translateX(0); }
        50% { transform: translate(-50%, -50%) translateX(5px); }
      }
      
      @keyframes truckBounce {
        0%, 100% { transform: translate(-50%, -50%) translateY(0) rotate(0deg); }
        25% { transform: translate(-50%, -50%) translateY(-3px) rotate(-5deg); }
        75% { transform: translate(-50%, -50%) translateY(-3px) rotate(5deg); }
      }
      
      @keyframes almostThere {
        0%, 100% { transform: scale(1); box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3); }
        50% { transform: scale(1.02); box-shadow: 0 4px 12px rgba(102, 126, 234, 0.5); }
      }
      
      @keyframes celebrate {
        0% { transform: scale(0) rotate(0deg); opacity: 0; }
        50% { transform: scale(1.2) rotate(180deg); opacity: 1; }
        100% { transform: scale(1) rotate(360deg); opacity: 1; }
      }
      
      @keyframes confettiFall {
        0% { 
          transform: translate(0, 0) rotate(0deg);
          opacity: 1;
        }
        100% { 
          transform: translate(${Math.random() * 200 - 100}px, 100px) rotate(720deg);
          opacity: 0;
        }
      }
      
      @keyframes shimmer {
        0% { opacity: 0.8; }
        50% { opacity: 1; }
        100% { opacity: 0.8; }
      }
      
      @keyframes bounceIn {
        0% { transform: scale(0.95); opacity: 0; }
        50% { transform: scale(1.02); }
        100% { transform: scale(1); opacity: 1; }
      }
      
      @keyframes fadeOutScale {
        to { opacity: 0; transform: scale(0.8); }
      }
      
      @keyframes pulseSuccess {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.02); }
      }
      
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-10px); }
        75% { transform: translateX(10px); }
      }
      
      @keyframes collapseUp {
        to { max-height: 0; opacity: 0; }
      }
      
      @keyframes expandDown {
        from { max-height: 0; opacity: 0; }
        to { max-height: 300px; opacity: 1; }
      }
      
      /* Responsive adjustments */
      @media (max-width: 768px) {
        .shipping-message-container,
        .shipping-message-free,
        .shipping-message-no-free {
          padding: 12px !important;
        }
        
        .shipping-progress-bar {
          height: 16px !important;
        }
        
        .truck-icon {
          font-size: 1.2em !important;
        }
        
        .progress-label {
          font-size: 0.75em !important;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

customElements.define('shipping-message', ShippingMessage);

// Add general animations if not already present
if (!document.querySelector('#cart-animations-style')) {
  const animationStyles = document.createElement('style');
  animationStyles.id = 'cart-animations-style';
  animationStyles.textContent = `
    input[type="number"] {
      transition: transform 0.2s ease, border-color 0.3s ease;
    }
    
    .cart-item {
      transition: all 0.3s ease;
    }
    
    .loading-overlay {
      transition: opacity 0.3s ease;
    }
    
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    @keyframes fadeOut {
      from { opacity: 1; }
      to { opacity: 0; }
    }
  `;
  document.head.appendChild(animationStyles);
}

// Initialize enhanced cart features on page load
document.addEventListener('DOMContentLoaded', () => {
  // Add intersection observer for scroll animations
  const observerOptions = {
    threshold: 0.1,
    rootMargin: '0px'
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.style.animation = 'fadeInUp 0.5s ease forwards';
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  // Observe cart elements
  document.querySelectorAll('.cart-item, .cart__footer, .cross-sell').forEach(el => {
    observer.observe(el);
  });

  // Enhanced checkout button click feedback
  document.querySelectorAll('.cart-checkout-button .button').forEach(button => {
    button.addEventListener('click', function(e) {
      if (!this.disabled) {
        // Add ripple effect
        const ripple = document.createElement('span');
        ripple.className = 'checkout-ripple';
        ripple.style.cssText = `
          position: absolute;
          background: rgba(255,255,255,0.5);
          border-radius: 50%;
          transform: scale(0);
          animation: rippleEffect 0.6s ease-out;
          pointer-events: none;
        `;
        
        const rect = this.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        ripple.style.width = ripple.style.height = size + 'px';
        ripple.style.left = (e.clientX - rect.left - size / 2) + 'px';
        ripple.style.top = (e.clientY - rect.top - size / 2) + 'px';
        
        this.style.position = 'relative';
        this.style.overflow = 'hidden';
        this.appendChild(ripple);
        
        setTimeout(() => ripple.remove(), 600);
      }
    });
  });
});

// Add ripple animation
const rippleStyle = document.createElement('style');
rippleStyle.textContent = `
  @keyframes rippleEffect {
    to {
      transform: scale(4);
      opacity: 0;
    }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
document.head.appendChild(rippleStyle);

console.log('Nutriop Longevity Enhanced Cart v3.0 with Dynamic Progress Bar loaded successfully');